import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Pencil, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DutyCodesPage() {
  const { fleetId } = useParams();
  const navigate = useNavigate();
  const [fleet, setFleet] = useState(null);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [newCode, setNewCode] = useState({ code: '', label: '', color: '#3B82F6' });

  const fetchData = useCallback(async () => {
    try {
      const [fleetRes, codesRes] = await Promise.all([
        axios.get(`${API_URL}/api/fleets/${fleetId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/duty-codes?fleet_id=${fleetId}`, { withCredentials: true })
      ]);
      setFleet(fleetRes.data);
      setCodes(codesRes.data);
    } finally {
      setLoading(false);
    }
  }, [fleetId, setFleet, setCodes, setLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/duty-codes`, { ...newCode, fleet_id: fleetId }, { withCredentials: true });
      toast.success('Duty code added');
      setNewCode({ code: '', label: '', color: '#3B82F6' });
      setShowAddModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to add duty code');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/duty-codes/${editingCode.id}`, editingCode, { withCredentials: true });
      toast.success('Duty code updated');
      setEditingCode(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (codeId) => {
    if (!window.confirm('Delete this duty code?')) return;
    try {
      await axios.delete(`${API_URL}/api/duty-codes/${codeId}`, { withCredentials: true });
      toast.success('Deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-16 h-16 border-4 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate(`/fleet/${fleetId}/schedule`)} className="p-2 hover:bg-gray-100 rounded-sm">
            <ArrowLeft size={24} />
          </button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-[#111827]">{fleet?.name} - Duty Codes</h1>
          </div>
          <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white rounded-sm hover:bg-[#001f7a]">
            <Plus size={20} /> Add Code
          </button>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E5E7EB]">
                <th className="text-left p-4 font-semibold">Color</th>
                <th className="text-left p-4 font-semibold">Code</th>
                <th className="text-left p-4 font-semibold">Label</th>
                <th className="text-center p-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.map(code => (
                <tr key={code.id} className="border-b border-[#E5E7EB] hover:bg-gray-50">
                  <td className="p-4">
                    <div className="w-10 h-10 rounded border border-gray-300" style={{ backgroundColor: code.color }}></div>
                  </td>
                  <td className="p-4 font-bold">{code.code}</td>
                  <td className="p-4">{code.label}</td>
                  <td className="p-4 text-center">
                    <button onClick={() => setEditingCode(code)} className="p-2 text-[#002FA7] hover:bg-gray-100 rounded-sm mr-2">
                      <Pencil size={18} />
                    </button>
                    <button onClick={() => handleDelete(code.id)} className="p-2 text-[#FF2A2A] hover:bg-gray-100 rounded-sm">
                      <Trash size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-[#111827] mb-4">Add Duty Code</h3>
            <form onSubmit={handleCreate}>
              <input type="text" placeholder="Code (e.g., A1)" value={newCode.code} onChange={(e) => setNewCode({...newCode, code: e.target.value})} className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm mb-3" required />
              <input type="text" placeholder="Label (e.g., Alert 1)" value={newCode.label} onChange={(e) => setNewCode({...newCode, label: e.target.value})} className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm mb-3" required />
              <input type="color" value={newCode.color} onChange={(e) => setNewCode({...newCode, color: e.target.value})} className="w-full h-12 border border-[#E5E7EB] rounded-sm mb-4" />
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="px-4 py-2 border border-[#E5E7EB] rounded-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-[#002FA7] text-white rounded-sm">Add</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingCode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-[#111827] mb-4">Edit Duty Code</h3>
            <form onSubmit={handleUpdate}>
              <input type="text" value={editingCode.code} onChange={(e) => setEditingCode({...editingCode, code: e.target.value})} className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm mb-3" required />
              <input type="text" value={editingCode.label} onChange={(e) => setEditingCode({...editingCode, label: e.target.value})} className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm mb-3" required />
              <input type="color" value={editingCode.color} onChange={(e) => setEditingCode({...editingCode, color: e.target.value})} className="w-full h-12 border border-[#E5E7EB] rounded-sm mb-4" />
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setEditingCode(null)} className="px-4 py-2 border border-[#E5E7EB] rounded-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-[#002FA7] text-white rounded-sm">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
